import React, { forwardRef } from 'react';
import './I9Form.css';
import { i9TemplateLogo } from '../../../../assets/images';
import useAuth from '../../../hooks/useAuth';
import { valueMap } from '../mapping';
import { useLocation } from 'react-router-dom';

const I9FormTemplate = forwardRef((props, ref) => {
  const { data } = props;
  const { userConfig } = useAuth();

  const location = useLocation();
  
  const jobDetails = data?.['jobDetails&I9Section1'] || {}
  const i9Docs = data?.i9Documents || {};
  const listA = i9Docs?.selectList === 'listA';
  const listBC = i9Docs?.selectList === 'listBc';

  const isSupervisor = location?.pathname?.includes('i9-submissions')

  return (
    <div ref={ref} className="i9-form-template">
      <div className="form-container">
        <div className="header">
          <div><img src={i9TemplateLogo} alt="I-9 Logo" /></div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <h1 style={{ width: 286 }}>Employment Eligibility Verification</h1>
            <p style={{ width: 216 }}><strong>Department of Homeland Security</strong></p>
            <p style={{ fontSize: 14, width: 270 }}>U.S. Citizenship and Immigration Services</p>
          </div>
          <div style={{ width: 94 }}>
            <h1 style={{ fontSize: 12 }}>USCIS</h1>
            <h1 style={{ fontSize: 12 }}>Form I-9</h1>
            <p style={{ fontSize: 10 }}>OMB No. 1615-0047 Expires 05/31/2027</p>
          </div>
        </div>

        <div className="legal-notice">
          <div className="start-here">START HERE: Employers must ensure the form instructions are available to employees when completing this form. Employers are liable for failing to comply with the requirements for completing this form. See below and the Instructions.</div>
          <div className="anti-discrimination"><strong>ANTI-DISCRIMINATION NOTICE:</strong> All employees can choose which acceptable documentation to present for Form I-9. Employers cannot ask employees for documentation to verify information in <strong>Section 1</strong>, or specify which acceptable documentation employees must present for <strong>Section 2</strong> or Supplement B, Reverification and Rehire. Treating employees differently based on their citizenship, immigration status, or national origin may be illegal.</div>
        </div>

        <div className="form-section" style={{ borderBottom: '5px solid #000' }}>
          <div className="section-title"><strong>Section 1. Employee Information and Attestation:</strong> Employees must complete and sign Section 1 of Form I-9 no later than the <strong>first day of employment</strong>, but not before accepting a job offer.</div>

          <div className="form-row">
            <div className="form-group border-right">
              <label>Last Name (Family Name)</label>
              <div style={{ width: 190 }} className="form-value">{jobDetails?.lastName}</div>
            </div>
            <div className="form-group border-right">
              <label>First Name (Given Name)</label>
              <div style={{ width: 170 }} className="form-value">{jobDetails?.firstName}</div>
            </div>
            <div className="form-group border-right">
              <label>Middle Initial (if any)</label>
              <div className="form-value">{jobDetails?.middleName}</div>
            </div>
            <div className="form-group">
              <label>Other Last Names Used (if any)</label>
              <div style={{ width: 190 }} className="form-value">{jobDetails?.otherNames || '-'}</div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group border-right">
              <label>Address (Street Number and Name)</label>
              <div className="form-value" style={{ width: 230 }}>{jobDetails?.address}</div>
            </div>
            <div className="form-group border-right">
              <label>Apt. Number (if any)</label>
              <div style={{ width: 85 }} className="form-value">{jobDetails?.apartNumber || '-'}</div>
            </div>
            <div className="form-group border-right">
              <label>City or Town</label>
              <div className="form-value" style={{ width: 180 }}>{jobDetails?.city || '-'}</div>
            </div>
            <div className="form-group border-right" style={{ width: 65 }}>
              <label>State</label>
              <div className="form-value">{jobDetails?.state || '-'}</div>
            </div>
            <div className="form-group">
              <label>ZIP Code</label>
              <div className="form-value">{jobDetails?.zipCode || '-'}</div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group border-right">
              <label>Date of Birth (mm/dd/yyyy)</label>
              <div className="form-value">{jobDetails?.dob ? new Date(jobDetails.dob).toLocaleDateString() : '-'}</div>
            </div>
            <div className="form-group border-right">
              <label>U.S. Social Security Number</label>
              <div className="form-value">{jobDetails?.ssnNumber || '-'}</div>
            </div>
            <div className="form-group border-right">
              <label>Employee's Email Address</label>
              <div style={{ width: 230 }} className="form-value">{data?.email || '-'}</div>
            </div>
            <div className="form-group">
              <label>Employee's Telephone Number</label>
              <div className="form-value">{data?.phone || '-'}</div>
            </div>
          </div>

          <div className="form-row">
            <div className="border-right" style={{ flex: 1, padding: '5px 0 5px 10px' }}>
              <div style={{ width: 185, height: 140, fontSize: '8.5pt', fontWeight: 'bold', lineHeight: '120%' }}>
                I am aware that federal law provides for imprisonment and/or fines for false statements, or the use of false documents, in connection with the completion of this form. I attest, under penalty of perjury, that this information, including my selection of the box attesting to my citizenship or immigration status, is true and correct.
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '7pt', width: 520 }}>
                <div style={{ padding: '0px 5px' }}>Check one of the following boxes to attest to your citizenship or immigration status (See page 2 and 3 of the instructions.):</div>

                <div className="checkbox-group">
                  <div className={`checkbox ${jobDetails?.citizenshipStatus === 'unitedState' ? 'checked' : ''}`}></div>
                  <div className="checkbox-label">1. A citizen of the United States</div>
                </div>
                <div className="checkbox-group">
                  <div className={`checkbox ${jobDetails?.citizenshipStatus === 'presidentResident' ? 'checked' : ''}`}></div>
                  <div className="checkbox-label">2. A noncitizen national of the United States (See Instructions.)</div>
                </div>
                <div className="checkbox-group">
                  <div className={`checkbox ${jobDetails?.citizenshipStatus === 'ead' ? 'checked' : ''}`}></div>
                  <div className="checkbox-label">3. A lawful permanent resident (Enter USCIS or A-Number):</div>
                  <div className="form-value" style={{ width: 150, marginLeft: 5 }}>{jobDetails?.aNumber ? 'A-Number' : jobDetails?.usic ? 'USCIS' : '-'}</div>
                </div>
                <div className="checkbox-group">
                  <div className={`checkbox ${jobDetails?.citizenshipStatus === 'temporaryVisa' ? 'checked' : ''}`}></div>
                  <div className="checkbox-label">4. A noncitizen (other than Item Numbers 2. and 3. above) authorized to work until (exp. date, if any):</div>
                </div>

                <div style={{ padding: '5px 5px 5px 10px' }}>If you check <strong>Item Number 4.,</strong> enter one of these:</div>

                <div style={{ paddingLeft: 10, display: 'flex', flexWrap: 'wrap' }}>
                  <div className="if-item-4 border-right">
                    <div style={{ padding: 2, width: 85, textAlign: 'center' }}><strong>USCIS A-Number</strong></div>
                    <div className="form-value border-top" style={{ padding: 3, }}>{jobDetails.aNumber || jobDetails?.usic || '-'}</div>
                  </div>
                  <div style={{ alignSelf: 'center', margin: '0 2px' }}>OR</div>
                  <div className="if-item-4 border-right">
                    <div style={{ padding: 2, width: 140, textAlign: 'center' }}><strong>Form I-94 Admission Number</strong></div>
                    <div className="form-value border-top" style={{ padding: 3 }}>{data.admissionNumber || '-'}</div>
                  </div>
                  <div style={{ alignSelf: 'center', margin: '0 2px' }}>OR</div>
                  <div className="if-item-4">
                    <div style={{ padding: 2, width: 240, textAlign: 'center' }}><strong>Foreign Passport Number and Country of Issuance</strong></div>
                    <div className="form-value border-top" style={{ padding: 3 }}>{data.passportInfo || '-'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="form-row">
            {/* <div className="form-group border-right" style={{ display: 'flex' }}>
              <label>Signature of Employee</label>
              <div className="form-value" style={{ minHeight: 30, marginLeft: 5 }}>
                { data.workmanEsign
                ? <img src={`${data.workmanEsign}`} alt="signature" style={{ maxHeight: 25, maxWidth: 120 }} />
                : null}
              </div>
            </div> */}

            <div className="form-group border-right" style={{ display: 'flex' }}>
           <label>Signature of Employee</label>
          <div className="form-value" style={{ minHeight: 30, marginLeft: 5 }}>
      <img
        src={`${data.esignature}`}
        alt="signature"
        style={{ maxHeight: 35, maxWidth: 120 }}
      />
  </div>
</div>

            <div className="form-group">
              <label>Today's Date (mm/dd/yyyy)</label>
              <div style={{ width: 270 }} className="form-value">{new Date().toLocaleDateString()}</div>
            </div>
          </div>

          <div style={{ fontSize: '7pt', fontWeight: 'bold', padding: 5, textAlign: 'center' }}>
            If a preparer and/or translator assisted you in completing Section 1, that person MUST complete the Preparer and/or Translator Certification on Page 3.
          </div>
        </div>

        <div className="form-section" style={{ borderBottom: '0px solid hsl(0, 0%, 99%)' }}>
          <div className="section-title"><strong>Section 2. Employer Review and Verification:</strong> Employers or their authorized representative must complete and sign <strong>Section 2</strong> within three business days after the employee's first day of employment, and must physically examine, or examine consistent with an alternative procedure authorized by the Secretary of DHS, documentation from List A OR a combination of documentation from List B and List C. Enter any additional documentation in the Additional Information box, see Instructions.</div>
          <div className="form-row">
            <div className="form-group-zero-pad">
              <div style={{ fontSize: '9pt', paddingLeft: 20 }}><strong>List A</strong></div>
              <div style={{ display: 'flex' }}>
                <div className="document-key"><strong>Document Title 1</strong></div>
                <div className="document-value">{(listA && i9Docs?.listDocument && valueMap[i9Docs?.listDocument]) || '-'}</div>
              </div>
              <div style={{ display: 'flex' }}>
                <div className="document-key">Issuing Authority</div>
                <div className="document-value">{'-'}</div>
              </div>
              <div style={{ display: 'flex' }}>
                <div className="document-key">Document Number (if any)</div>
                <div className="document-value">{(listA && i9Docs?.documentNumber1) || '-'}</div>
              </div>
              <div style={{ display: 'flex' }}>
                <div className="document-key">Expiration Date (if any)</div>
                <div className="document-value">{listA ? new Date(i9Docs?.expirationDate1).toLocaleDateString() : '-'}</div>
              </div>
              <div style={{ display: 'flex' }}>
                <div className="document-key"><strong>Document Title 2</strong></div>
                <div className="document-value">{'-'}</div>
              </div>
              <div style={{ display: 'flex' }}>
                <div className="document-key">Issuing Authority</div>
                <div className="document-value">{'-'}</div>
              </div>
              <div style={{ display: 'flex' }}>
                <div className="document-key">Document Number (if any)</div>
                <div className="document-value">{'-'}</div>
              </div>
              <div style={{ display: 'flex' }}>
                <div className="document-key">Expiration Date (if any)</div>
                <div className="document-value">{'-'}</div>
              </div>
              <div style={{ display: 'flex' }}>
                <div className="document-key"><strong>Document Title 3</strong></div>
                <div className="document-value">{'-'}</div>
              </div>
              <div style={{ display: 'flex' }}>
                <div className="document-key">Issuing Authority</div>
                <div className="document-value">{'-'}</div>
              </div>
              <div style={{ display: 'flex' }}>
                <div className="document-key">Document Number (if any)</div>
                <div className="document-value">{'-'}</div>
              </div>
              <div style={{ display: 'flex' }}>
                <div className="document-key">Expiration Date (if any)</div>
                <div className="document-value">{'-'}</div>
              </div>
            </div>
            <div className="form-group-zero-pad">
              <div style={{ display: 'flex' }}>
                <div className="border-bottom" style={{ backgroundColor: '#BFC1C3', fontSize: '8pt', padding: 2.5 }}> <strong>OR</strong></div>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                    <div style={{ fontSize: '9pt' }}><strong>List B</strong></div>
                    <div style={{ fontSize: '9pt' }}><strong>AND</strong></div>
                    <div style={{ fontSize: '9pt' }}><strong>List C</strong></div>
                  </div>
                  <div style={{ display: 'flex' }}>
                    <div className="list-b-document">{listBC ? valueMap[i9Docs?.identityDocumnet] : '-'}</div>
                    <div className="list-b-document">{listBC ? valueMap[i9Docs?.workAuthorizationDocument] : '-'}</div>
                  </div>
                  <div style={{ display: 'flex' }}>
                    <div className="list-b-document">{(listBC && i9Docs?.issuingAuthority1) || '-'}</div>
                    <div className="list-b-document">{(listBC && i9Docs?.issuingAuthority2) || '-'}</div>
                  </div>
                  <div style={{ display: 'flex' }}>
                    <div className="list-b-document">{(listBC && i9Docs?.documentNumber2) || '-'}</div>
                    <div className="list-b-document">{(listBC && i9Docs?.documentNumber3) || '-'}</div>
                  </div>
                  <div style={{ display: 'flex' }}>
                    <div className="list-b-document border-bottom">{(listBC && (i9Docs?.expirationDate2 && new Date(i9Docs?.expirationDate2).toLocaleDateString())) || '-'}</div>
                    <div className="list-b-document border-bottom">-</div>
                  </div>
                </div>
              </div>
              <div className="section-title"><strong>Additional Information</strong></div>
              <div style={{ height: 183 }}></div>
              <div style={{ display: 'flex', paddingLeft: 5 }}>
                <div className="checkbox"></div>
                <div className="checkbox-label" style={{ fontSize: '6.5pt' }}>Check here if you used an alternative procedure authorized by DHS to examine documents.</div>
              </div>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group-zero-pad border-right">
              <div style={{ width: 555, fontSize: '7pt', padding: '5px 10px 5px 2px' }}>
                <strong>Certification: I attest, under penalty of perjury, that (1) I have examined the documentation presented by the above-named employee, (2) the above-listed documentation appears to be genuine and to relate to the employee named, and (3) to the best of my knowledge, the employee is authorized to work in the United States. </strong>
              </div>
            </div>
            <div className="form-group-zero-pad">
              <div style={{ fontSize: '7pt', padding: '2px 5px' }}>First Day of Employment (mm/dd/yyyy):</div>
              <div className="form-value"></div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group border-right" style={{ flex: 1.8 }}>
              <label>Last Name, First Name and Title of Employer or Authorized Representative</label>
              <div className="form-value">{ isSupervisor && `${userConfig?.firstName} ${userConfig?.lastName} ,${' '} ${userConfig?.role?.charAt(0).toUpperCase() + userConfig?.role?.slice(1)}`}</div>
            </div>
            <div className="form-group border-right" style={{ flex: 1.3 }}>
              <label>Signature of Employer or Authorized Representative</label>
              <div className="form-value" style={{ minHeight: 20 }}>
                {data.supervisorEsign
                  ? <img src={`${data.supervisorEsign}`} alt="signature" style={{ maxHeight: 30, maxWidth: 120 }} />
                  : null}
              </div>
            </div>
            <div className="form-group" style={{ flex: 0.7 }}>
              <label>Today's Date (mm/dd/yyyy)</label>
              <div className="form-value">{new Date().toLocaleDateString()}</div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group border-right" style={{ flex: 2 }}>
              <label>Employer's Business or Organization Name</label>
              <div className="form-value" style={{ minHeight: 20 }}>
                Freeman
                {/* {data?.contractorDetails?.name} */}
              </div>
            </div>
            <div className="form-group" style={{ flex: 2 }}>
              <label>Employer's Business or Organization Address, City or Town, State, ZIP Code</label>
              <div className="form-value" style={{ width: '418px' }}>
                International Plaza #2, 14221 Dallas Pkwy Suite 200, Dallas, TX 75254, USA
                {/* {data?.contractorDetails?.address} */}
              </div>
            </div>
          </div>
        </div>

        <div className="border-bottom" style={{ fontSize: '7pt', fontWeight: 'bold', padding: 2, textAlign: 'center' }}>
          For reverification or rehire, complete Supplement B, Reverification and Rehire on Page 4.
        </div>

        <div className="footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <p>Form I-9 (05/31/2027)</p> <p>Page 1 of 4</p>
        </div>
      </div>
    </div>
  );
});

export default I9FormTemplate;